/**
 * Logic thuần của bộ lọc "chỉ tập đang mở" ở 4 màn Bank (260819) — tách khỏi file import antd để
 * jest của react-scripts chạy được.
 *
 * Bank gắn với TRUYỆN chứ không gắn tập (cố ý: tập sau tái dùng đồ đã làm, đo trên prod 0 look trùng tên
 * qua 11 tập). Cái giá là danh sách chỉ dài thêm — bản quét novel→screenplay ra 32 nhân vật / 73 bối cảnh
 * cho một bộ, và 20/96 nhân vật chỉ xuất hiện đúng một tập.
 */

/**
 * Lọc danh sách theo tập.
 *
 * 🔴 `filterable = false` nghĩa là KHÔNG CÓ CĂN CỨ để lọc (ví dụ tập chưa gán trang phục) — khác hẳn
 * "lọc ra rỗng". Đo trên dev: plan dựng trước 260819 cho ra 0/6 bộ đồ; cứ thế lọc thì tủ đồ hiện trống
 * và người dùng tưởng mất sạch đồ. Ca đó phải trả về nguyên danh sách.
 */
export function filterByEpisode<T extends { id: string }>(
    items: T[],
    usedIds: string[] | undefined,
    opts: {
        on: boolean;
        filterable: boolean;
        /**
         * Lọc ra RỖNG thì coi là hỏng, không phải là câu trả lời — bật cho nhân vật và bối cảnh (tập nào
         * cũng phải có người và có nơi chốn; ra 0 nghĩa là khớp tên trượt, ví dụ nhân vật bị đổi tên sau
         * khi tạo plan). Tắt cho đạo cụ: tập không có đạo cụ nào là chuyện bình thường.
         *
         * Đo trên prod 260819: 65/65 plan lọc ra kết quả hợp lý, chưa plan nào rơi vào ca này — nhưng
         * hậu quả của nó là màn Bank trống trơn, quá đắt so với ba dòng phòng thủ.
         */
        emptyMeansAll?: boolean;
    },
): T[] {
    if (!opts.on || !opts.filterable || !usedIds) return items;
    const set = new Set(usedIds);
    const kept = items.filter((i) => set.has(i.id));
    if (!kept.length && items.length && opts.emptyMeansAll) return items;
    return kept;
}

export interface FilterHint {
    /** Chữ cạnh công tắc. */
    label: string;
    /** Giải thích khi di chuột — luôn nói được vì sao đang thấy chừng này. */
    hint: string;
    /** Công tắc có bấm được không. */
    enabled: boolean;
}

/** Chữ cho công tắc: nói thẳng đang thấy bao nhiêu trên tổng bao nhiêu, và vì sao. */
export function filterHint(shown: number, total: number, opts: { on: boolean; filterable: boolean }): FilterHint {
    if (!opts.filterable) {
        return {
            label: 'Chỉ tập này',
            hint: 'Tập này chưa có căn cứ để lọc — đang xem cả bộ.',
            enabled: false,
        };
    }
    if (!opts.on) {
        return {
            label: `Chỉ tập này`,
            hint: `Đang xem cả ${total} của bộ. Bật để chỉ xem thứ tập này dùng.`,
            enabled: true,
        };
    }
    return {
        label: `Chỉ tập này · ${shown}/${total}`,
        hint: `Còn ${total - shown} thứ nữa trong bộ mà tập này không dùng.`,
        enabled: true,
    };
}
